import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ShoppingBag, Heart, Gift, ExternalLink } from 'lucide-react';

const ShopWishlist = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-mauve-100 to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-darkslate-700 mb-4">
            KBK Basket Shop
          </h1>
          <p className="text-lg text-darkslate-500">All items are sourced from a local pet store we love and trust, meaning your support helps provide essential supplies for pets in need while also supporting a valued business in our community.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">

          {/* CTA Button - Top */}
          <div className="text-center mb-12" data-testid="shop-cta-top">
            <a
              href="https://www.zeffy.com/en-CA/ticketing/kibbles-and-bits-of-kindnesss-shop"
              target="_blank"
              rel="noopener noreferrer">
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-12 py-6 text-xl border-2 border-black shadow-lg">
                <ShoppingBag className="mr-3 h-6 w-6" />
                Shop KBK Basket Shop Now
                <ExternalLink className="ml-3 h-5 w-5" />
              </Button>
            </a>
            <p className="text-sm text-darkslate-400 mt-4">
              Opens in a new window • Powered by Zeffy
            </p>
          </div>
          
          {/* Welcome Message */}
          <Card className="border-2 border-olive-300 bg-olive-50 mb-8" data-testid="welcome-card">
            <CardHeader>
              <CardTitle className="text-2xl text-darkslate-700">
                Welcome to the KBK Basket Shop
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-darkslate-600">
              <p className="text-lg">

              </p>
              <p>We are a community-powered animal support pantry serving Airdrie and surrounding areas. 

The KBK Basket Shop was created so that anyone, both near and far, can easily support pets in need within our community.</p>
              <p>
                When you purchase a basket from our shop, you're helping us provide essential pet supplies to animals and families who rely on the support of Kibbles & Bits of Kindness.
              </p>
              <p>
                While each basket represents a thoughtful collection of helpful items, supplies may sometimes be distributed individually or combined with other donations. This allows us to support as many pets as possible and respond quickly to urgent needs in our community and surrounding areas.
              </p>
            </CardContent>
          </Card>

          {/* Your Purchase Helps */}
          <Card className="border-2 border-mauve-300 mb-8" data-testid="purchase-helps-card">
            <CardHeader>
              <CardTitle className="text-xl text-darkslate-700 flex items-center gap-2">
                <Heart className="h-6 w-6 text-red-600 fill-red-600" />
                Your Purchase May Help Support Pets In Situations Such As:
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-darkslate-600">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Families experiencing temporary financial hardship
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Emergency pet food assistance
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Seniors on fixed incomes caring for their pets
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  People facing unexpected veterinary expenses
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Families going through job loss or personal crisis
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Pets belonging to unhoused or vulnerably housed individuals
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Domestic violence situations where pets are staying with their families
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Newly rescued animals needing starter supplies
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Pet owners affected by emergencies or disasters
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2"></span>
                  
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Animals waiting for placement with rescues or foster homes
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  Urgent cases referred by community partners
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* How It Works */}
          <div className="bg-mauve-50 rounded-lg p-8 mb-8" data-testid="how-it-works">
            <h2 className="text-2xl font-bold text-darkslate-700 mb-6 text-center">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-darkslate-700 mb-2">Browse Our Baskets</h3>
                <p className="text-sm text-darkslate-500">
                  Click the button above to see all the basket options available.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-olive-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                  2
                </div>
                <h3 className="font-semibold text-darkslate-700 mb-2">Give a Basket</h3>
                <p className="text-sm text-darkslate-500">
                  Select a basket you'd like to donate and complete your order through Zeffy.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                  3
                </div>
                <h3 className="font-semibold text-darkslate-700 mb-2">Make an Impact</h3>
                <p className="text-sm text-darkslate-500">
                  Your donation helps animals get what they need. Thank you!
                </p>
              </div>
            </div>
          </div>

          {/* Donate Points Feature */}
          <Card className="border-2 border-amber-500 mb-8" data-testid="donate-points-card">
            <CardHeader>
              <CardTitle className="text-xl text-darkslate-700 flex items-center gap-2">
                <Gift className="h-6 w-6 text-amber-500" />
                Bonus: Donate Your Points!
              </CardTitle>
            </CardHeader>
            <CardContent className="text-darkslate-600">
              <p className="text-lg">
                When you shop at Doodle Dogs, you can <strong>donate your points to us</strong>! 
                It's another easy way to help animals.
              </p>
            </CardContent>
          </Card>

          {/* Thank You Message - Bottom */}
          <div className="text-center p-6 bg-mauve-50 rounded-lg" data-testid="thank-you-section">
            <p className="text-lg text-darkslate-700 mb-2">
              <strong>Thank you for supporting our mission.</strong>
            </p>
            <p className="text-darkslate-500 mb-4">
              Your kindness helps keep pets fed and cared for.
            </p>
            <p className="text-2xl font-bold text-darkslate-700">
              Happy shopping!
            </p>
            <p className="text-darkslate-400 mt-2">

            </p>
          </div>

        </div>
      </section>
    </div>);
};

export default ShopWishlist;
